import React from 'react';
import { Document, Page, StyleSheet, View, Text } from '@react-pdf/renderer';

// Renders the "page 1" summary of a voucher, styled to match the web app
// (theme.ts palette, VoucherStateBadge, ApprovalStages, CostCategories cards).
// Attachment pages are appended separately by voucherPdfExport.ts, which
// merges this document with the voucher's actual receipt files (images/PDFs)
// into one downloadable PDF.

const COLORS = {
  primary: '#1E5A6B',
  primaryDark: '#123640',
  success: '#2E8B57',
  info: '#2F6FED',
  warning: '#B8790A',
  error: '#C4432B',
  textPrimary: '#1B2A2E',
  textSecondary: '#5B6D70',
  divider: '#E0E0E0',
};

const STATE_STYLES = {
  DRAFT: { color: COLORS.textSecondary, bg: '#FFFFFF' },
  SUBMITTED: { color: COLORS.textSecondary, bg: '#EDEFEA' },
  IN_PROGRESS: { color: COLORS.warning, bg: '#FBF0DC' },
  APPROVED: { color: COLORS.info, bg: '#E4EBFC' },
  PAID: { color: COLORS.success, bg: '#E3F1EA' },
  REJECTED: { color: COLORS.error, bg: '#F8E7E4' },
};

const STAGE_COLORS = {
  done: { fg: '#1F7A5C', bg: '#E3F1EA' },
  current: { fg: '#A8710E', bg: '#FBF0DC' },
  upcoming: { fg: '#8B968C', bg: '#EDEFEA' },
  rejected: { fg: '#AF3A2E', bg: '#F8E7E4' },
  voided: { fg: '#8B968C', bg: '#F4F4F4' },
};

const STRIPE_COLORS = [
  '#2F6F62',
  '#96703E',
  '#5B87C9',
  '#8659BD',
  '#BD5F5F',
  '#4C9A8F',
];

const formatDate = (dateValue) => {
  if (!dateValue) return null;
  const date = new Date(isNaN(dateValue) ? dateValue : Number(dateValue));
  if (isNaN(date.getTime())) return null;
  return date.toISOString().split('T')[0];
};

const initials = (person) => {
  const value = `${person?.first_name?.[0] || ''}${person?.last_name?.[0] || ''}`;
  return value.toUpperCase() || '?';
};

// Mirrors ApprovalStages.tsx: a "created/submitted" node followed by one
// node per approval, colored by whether it's done/current/rejected/voided.
function buildStages(voucher) {
  const approvals = [...(voucher.voucher_approvals || [])].sort(
    (a, b) => (a.order_number || 0) - (b.order_number || 0)
  );

  const rejectedIndex = approvals.findIndex((a) => a.status === 'REJECTED');
  const firstPendingIndex = approvals.findIndex(
    (a) => a.status !== 'APPROVED' && a.status !== 'PAID' && a.status !== 'REJECTED'
  );

  const stages: any[] = [];

  const creator = voucher.created_by;
  if (creator?.id) {
    stages.push({
      key: 'created',
      label: voucher.submitted_at ? 'Submitted' : 'Created',
      name: `${creator.first_name || ''} ${creator.last_name || ''}`.trim() || 'Unknown',
      date: formatDate(voucher.submitted_at || voucher.created_at),
      state: 'done',
      person: creator,
    });
  }

  approvals.forEach((approval, index) => {
    const { approver, approval_stage, status, approved_at, comment } = approval;
    let state;
    if (status === 'APPROVED' || status === 'PAID') {
      state = 'done';
    } else if (status === 'REJECTED') {
      state = 'rejected';
    } else if (rejectedIndex !== -1 && index > rejectedIndex) {
      state = 'voided';
    } else if (index === firstPendingIndex) {
      state = 'current';
    } else {
      state = 'upcoming';
    }

    stages.push({
      key: `approval-${index}`,
      label: approval_stage?.is_payment_stage ? 'Payment' : 'Approval',
      name: approver ? `${approver.first_name || ''} ${approver.last_name || ''}`.trim() : 'Unassigned',
      date: formatDate(approved_at),
      state,
      person: approver,
      comment: status === 'REJECTED' ? comment : null,
    });
  });

  return stages;
}

const InfoCell = ({ label, value }) =>
  value ? (
    <View style={styles.infoCell} wrap={false}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={styles.infoValue}>{value}</Text>
    </View>
  ) : null;

const SectionTitle = ({ children }) => (
  <Text style={styles.sectionTitle}>{children}</Text>
);

const StageChip = ({ stage }) => {
  const colors = STAGE_COLORS[stage.state];
  const isVoided = stage.state === 'voided';
  return (
    <View
      wrap={false}
      style={[
        styles.stageChip,
        {
          backgroundColor: colors.bg,
          borderColor: stage.state === 'current' ? colors.fg : 'transparent',
          opacity: isVoided ? 0.6 : 1,
        },
      ]}
    >
      <View style={[styles.stageAvatar, { borderColor: colors.fg }]}>
        <Text style={[styles.stageAvatarText, { color: colors.fg }]}>
          {initials(stage.person)}
        </Text>
      </View>
      <View>
        <Text style={styles.stageName}>{stage.name}</Text>
        <Text style={styles.stageMeta}>
          {stage.label}
          {stage.date ? ` · ${stage.date}` : ''}
        </Text>
      </View>
    </View>
  );
};

const PDFDoc = ({ voucher }) => {
  const stateStyle = STATE_STYLES[voucher.state] || STATE_STYLES.SUBMITTED;
  const stages = buildStages(voucher);
  const rejectedStage = stages.find((s) => s.state === 'rejected' && s.comment);

  const seenParents: string[] = [];
  const getStripeColor = (parentName) => {
    let index = seenParents.indexOf(parentName);
    if (index === -1) {
      index = seenParents.length;
      seenParents.push(parentName);
    }
    return STRIPE_COLORS[index % STRIPE_COLORS.length];
  };

  const infoFields = [
    { label: 'Claimant', value: voucher.claimed_by ? `${voucher.claimed_by.first_name} ${voucher.claimed_by.last_name}` : null },
    { label: 'Department', value: voucher.department?.name },
    { label: 'Branch (Jamat)', value: voucher.branch },
    { label: 'Financial Year', value: voucher.financial_year?.year },
    { label: 'Expense Period', value: voucher.expense_period != null ? String(voucher.expense_period) : null },
    { label: 'Created', value: formatDate(voucher.created_at) },
    { label: 'Pay To', value: voucher.pay_to },
    { label: 'Account Number', value: voucher.receivers_account_number },
    { label: 'Payment Reference', value: voucher.payment_reference },
    { label: 'Paid On', value: formatDate(voucher.paid_by_amin) },
  ].filter((f) => f.value);

  const attachments = (voucher.voucher_cost_category || []).flatMap((category) =>
    (category.receipts || []).map((receipt) => ({
      fileName: receipt.path ? receipt.path.split('/').pop() : receipt.path,
      subCategory: category.sub_cost_category?.name || 'N/A',
    }))
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.headerBar}>
          <View style={styles.headerRow}>
            <View style={{ flexShrink: 1 }}>
              <Text style={styles.headerRef}>
                {voucher.reference_number ? `Voucher #${voucher.reference_number}` : 'Voucher (Draft)'}
              </Text>
              <Text style={styles.headerSub}>
                {voucher.department?.name || ''}
                {voucher.claimed_by
                  ? ` · ${voucher.claimed_by.first_name} ${voucher.claimed_by.last_name}`
                  : ''}
              </Text>
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              <Text style={styles.headerAmountLabel}>AMOUNT</Text>
              <Text style={styles.headerAmount}>
                {voucher.total_amount ?? '0'} {voucher.currency || 'SEK'}
              </Text>
            </View>
          </View>
          <View style={styles.headerRow}>
            <View
              style={[
                styles.stateBadge,
                { backgroundColor: stateStyle.bg, borderColor: stateStyle.color },
              ]}
            >
              <View style={[styles.stateDot, { backgroundColor: stateStyle.color }]} />
              <Text style={[styles.stateBadgeText, { color: stateStyle.color }]}>
                {voucher.state}
              </Text>
            </View>
          </View>
        </View>

        {/* Description */}
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>DESCRIPTION</Text>
          <Text style={styles.descriptionText}>
            {voucher.description || 'No description provided.'}
          </Text>
        </View>

        {/* Info grid */}
        <View style={[styles.infoGrid, styles.section]}>
          {infoFields.map((field, index) => (
            <InfoCell key={index} label={field.label} value={field.value} />
          ))}
        </View>

        {/* Cost Categories */}
        <View style={styles.section}>
          <SectionTitle>Cost Categories</SectionTitle>
          <View style={styles.cardGrid}>
            {(voucher.voucher_cost_category || []).map((category, index) => {
              const parentName = category.sub_cost_category?.cost_category?.name || 'Other';
              return (
                <View key={index} style={styles.cardWrapper} wrap={false}>
                  <View
                    style={[styles.costCard, { borderTopColor: getStripeColor(parentName) }]}
                  >
                    <View style={styles.headerRow}>
                      <Text style={styles.costCardTitle}>
                        {category.sub_cost_category?.name || 'N/A'}
                      </Text>
                      <Text style={styles.costCardAmount}>
                        {(voucher.currency || 'SEK')} {Number(category.cost_share ?? 0).toFixed(2)}
                      </Text>
                    </View>
                    <Text style={styles.costCardSubtitle}>
                      {parentName}
                      {category.sub_cost_category?.code ? ` · ${category.sub_cost_category.code}` : ''}
                    </Text>
                    {!!category.comment && (
                      <Text style={styles.costCardComment}>&ldquo;{category.comment}&rdquo;</Text>
                    )}
                  </View>
                </View>
              );
            })}
          </View>
          <View style={styles.totalBar} wrap={false}>
            <Text style={styles.totalBarLabel}>TOTAL</Text>
            <Text style={styles.totalBarValue}>
              {voucher.total_amount ?? '0'} {voucher.currency || 'SEK'}
            </Text>
          </View>
        </View>

        {/* Approval Progress */}
        {stages.length > 0 && (
          <View style={styles.section}>
            <SectionTitle>Approval Progress</SectionTitle>
            <View style={styles.stagesRow}>
              {stages.map((stage) => (
                <StageChip key={stage.key} stage={stage} />
              ))}
            </View>
            {rejectedStage && (
              <Text style={styles.rejectionNote}>
                <Text style={{ fontWeight: 'bold' }}>Rejection reason: </Text>
                {rejectedStage.comment}
              </Text>
            )}
          </View>
        )}

        {/* Attachments Manifest */}
        {attachments.length > 0 && (
          <View style={styles.section}>
            <SectionTitle>Attachments</SectionTitle>
            <Text style={styles.attachmentsNote}>
              The files below are attached to this voucher and appear as full pages after this summary.
            </Text>
            {attachments.map((attachment, index) => (
              <View key={index} style={styles.attachmentRow}>
                <Text style={styles.attachmentCategory}>{attachment.subCategory}</Text>
                <Text style={styles.attachmentFile}>{attachment.fileName}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            Generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 24,
    paddingBottom: 24,
    paddingHorizontal: 28,
    fontSize: 9,
    fontFamily: 'Helvetica',
    color: COLORS.textPrimary,
  },
  section: {
    marginBottom: 10,
  },

  // Header
  headerBar: {
    backgroundColor: COLORS.primaryDark,
    borderRadius: 6,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginBottom: 12,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerRef: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  headerSub: {
    fontSize: 8.5,
    color: '#B9D2D8',
    marginTop: 2,
  },
  headerAmountLabel: {
    fontSize: 7,
    fontWeight: 'bold',
    letterSpacing: 1,
    color: '#B9D2D8',
  },
  headerAmount: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginTop: 1,
  },
  stateBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    paddingVertical: 3,
    paddingHorizontal: 8,
    marginTop: 8,
  },
  stateDot: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
    marginRight: 5,
  },
  stateBadgeText: {
    fontSize: 8,
    fontWeight: 'bold',
  },

  // Section titles / description
  sectionTitle: {
    fontSize: 10.5,
    fontWeight: 'bold',
    color: COLORS.textPrimary,
    marginBottom: 6,
  },
  sectionLabel: {
    fontSize: 7,
    fontWeight: 'bold',
    letterSpacing: 0.8,
    color: COLORS.textSecondary,
    marginBottom: 3,
  },
  descriptionText: {
    fontSize: 9,
    lineHeight: 1.4,
    color: COLORS.textPrimary,
    borderLeftWidth: 2,
    borderLeftColor: COLORS.primary,
    paddingLeft: 8,
  },

  // Info grid
  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  infoCell: {
    width: '25%',
    paddingRight: 10,
    paddingBottom: 8,
  },
  infoLabel: {
    fontSize: 6.5,
    fontWeight: 'bold',
    letterSpacing: 0.6,
    color: COLORS.textSecondary,
    marginBottom: 2,
  },
  infoValue: {
    fontSize: 9,
    fontWeight: 'bold',
    color: COLORS.textPrimary,
  },

  // Cost category cards
  cardGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  cardWrapper: {
    width: '33.333%',
    paddingRight: 8,
    paddingBottom: 8,
  },
  costCard: {
    borderWidth: 1,
    borderColor: COLORS.divider,
    borderTopWidth: 3,
    borderRadius: 4,
    padding: 8,
  },
  costCardTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    flexShrink: 1,
  },
  costCardAmount: {
    fontSize: 9,
    fontWeight: 'bold',
  },
  costCardSubtitle: {
    fontSize: 7.5,
    color: COLORS.textSecondary,
    marginTop: 2,
  },
  costCardComment: {
    fontSize: 7.5,
    fontStyle: 'italic',
    color: COLORS.textSecondary,
    marginTop: 3,
  },
  totalBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: COLORS.primaryDark,
    borderRadius: 4,
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginTop: 2,
  },
  totalBarLabel: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  totalBarValue: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },

  // Approval stages
  stagesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  stageChip: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 20,
    paddingVertical: 4,
    paddingHorizontal: 8,
    paddingRight: 10,
    marginRight: 6,
    marginBottom: 6,
  },
  stageAvatar: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1.5,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 6,
  },
  stageAvatarText: {
    fontSize: 7,
    fontWeight: 'bold',
  },
  stageName: {
    fontSize: 8.5,
    fontWeight: 'bold',
    color: COLORS.textPrimary,
  },
  stageMeta: {
    fontSize: 7,
    color: COLORS.textSecondary,
    marginTop: 1,
  },
  rejectionNote: {
    fontSize: 8,
    color: COLORS.error,
    marginTop: 2,
  },

  // Attachments
  attachmentsNote: {
    fontSize: 7.5,
    color: COLORS.textSecondary,
    marginBottom: 4,
  },
  attachmentRow: {
    flexDirection: 'row',
    paddingVertical: 3,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.divider,
  },
  attachmentCategory: {
    fontSize: 8,
    color: COLORS.textSecondary,
    width: '30%',
  },
  attachmentFile: {
    fontSize: 8,
    color: COLORS.textPrimary,
  },

  // Footer
  footer: {
    marginTop: 8,
    borderTopWidth: 1,
    borderTopColor: COLORS.divider,
    paddingTop: 6,
  },
  footerText: {
    fontSize: 7,
    color: COLORS.textSecondary,
    textAlign: 'center',
  },
});

export default PDFDoc;
